import React, { useCallback, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { Download } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/Table';
import { showErrorToast } from '../../utils/errorHandler';
import {
  bulkCreateStudents,
  type RosterBulkCreateStudents422Body,
  type RosterBulkCreateStudentsResponse,
} from '../../modules/roster/api/roster.client';
import {
  buildRosterBulkStudentsPayloadFromParsed,
  parseBulkStudentsCsv,
  type ParseBulkStudentsCsvResult,
  type ParsedStudentCsvRow,
} from '../../pages/admin/studentBulkImportCsv';

export interface StudentBulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful import when at least one student was created (for list refetch). */
  onImportSuccess?: () => void | Promise<void>;
}

const SAMPLE_CSV = `first_name,last_name,email,phone,admission_no,gender,dob
Serin,Kumar,student@example.com,9685741230,,male,2018-01-02
`;

export const StudentBulkImportModal: React.FC<StudentBulkImportModalProps> = ({
  isOpen,
  onClose,
  onImportSuccess,
}) => {
  const [importFile, setImportFile] = useState<File | null>(null);
  const [parsedImport, setParsedImport] = useState<ParseBulkStudentsCsvResult | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [continueOnError, setContinueOnError] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [importApiResult, setImportApiResult] = useState<RosterBulkCreateStudentsResponse | null>(null);
  const [importPreflight422, setImportPreflight422] = useState<unknown[] | null>(null);

  const resetState = useCallback(() => {
    setImportFile(null);
    setParsedImport(null);
    setImportApiResult(null);
    setImportPreflight422(null);
    setSkipDuplicates(true);
    setContinueOnError(true);
    setIsImporting(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'students-sample.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onImportFileChange = async (file: File | null) => {
    setImportFile(file);
    setImportApiResult(null);
    setImportPreflight422(null);
    if (!file) {
      setParsedImport(null);
      return;
    }
    try {
      const text = await file.text();
      setParsedImport(parseBulkStudentsCsv(text));
    } catch {
      setParsedImport({
        rows: [],
        warnings: [],
        errors: ['Could not read the file.'],
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!parsedImport?.rows.length) {
      showErrorToast('Nothing to import.');
      return;
    }
    if (parsedImport.errors.length) {
      showErrorToast(parsedImport.errors[0] ?? 'Fix CSV errors before importing.');
      return;
    }
    setIsImporting(true);
    setImportPreflight422(null);
    try {
      const payload = buildRosterBulkStudentsPayloadFromParsed(
        parsedImport.rows,
        skipDuplicates,
        continueOnError
      );
      const res = await bulkCreateStudents(payload);
      setImportApiResult(res);
      if (res.summary.created > 0 && onImportSuccess) {
        await onImportSuccess();
      }
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 422) {
        const data = err.response.data as RosterBulkCreateStudents422Body;
        const list = Array.isArray(data?.errors) ? data.errors : [data];
        setImportPreflight422(list);
        return;
      }
      showErrorToast(err);
    } finally {
      setIsImporting(false);
    }
  };

  const importReady =
    parsedImport &&
    parsedImport.errors.length === 0 &&
    parsedImport.rows.length > 0 &&
    !isImporting;

  const previewRows = useMemo(() => parsedImport?.rows.slice(0, 50) ?? [], [parsedImport]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import students"
      size="lg"
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
          <p className="font-medium">Instructions</p>
          <ol className="list-decimal list-inside text-sm space-y-1">
            <li>
              Download the sample CSV. Required column: <strong>first_name</strong>. Optional:{' '}
              <strong>last_name</strong>, <strong>email</strong>, <strong>phone</strong>,{' '}
              <strong>admission_no</strong>, <strong>gender</strong>, <strong>dob</strong> (YYYY-MM-DD).
            </li>
            <li>
              Creates roster student records only. Does not send invites, enroll in classes, or link parents. Use
              Invite and Enroll separately.
            </li>
            <li>
              Header aliases: <strong>date_of_birth</strong> for <strong>dob</strong>,{' '}
              <strong>admission_number</strong> for <strong>admission_no</strong>. Other columns are ignored with a
              warning.
            </li>
            <li>Preview rows, then run import. Results stay visible until you close this dialog.</li>
          </ol>
        </div>

        <details className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <summary className="cursor-pointer font-medium text-gray-900 dark:text-white">Advanced options</summary>
          <div className="mt-3 space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={skipDuplicates}
                onChange={(e) => setSkipDuplicates(e.target.checked)}
              />
              <span>Skip duplicates (default: on)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={continueOnError}
                onChange={(e) => setContinueOnError(e.target.checked)}
              />
              <span>Continue on error (default: on)</span>
            </label>
          </div>
        </details>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          <div className="w-full">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Sample CSV</label>
            <button
              type="button"
              onClick={downloadSample}
              className="h-11 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
            >
              <Download className="h-5 w-5" />
              Download sample
            </button>
          </div>
          <div className="w-full">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">CSV file</label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="w-full sm:w-auto">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => void onImportFileChange(e.target.files?.[0] ?? null)}
                  className="hidden"
                />
                <span className="inline-flex w-full sm:w-auto h-11 items-center justify-center rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 text-sm font-medium text-gray-800 dark:text-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                  Choose file
                </span>
              </label>
              <div className="text-sm text-gray-600 dark:text-gray-300 truncate w-full sm:max-w-[220px]">
                {importFile ? importFile.name : 'No file chosen'}
              </div>
            </div>
          </div>
        </div>

        {parsedImport?.warnings.length ? (
          <div className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 rounded-lg px-3 py-2">
            {parsedImport.warnings.map((w, i) => (
              <p key={i}>{w}</p>
            ))}
          </div>
        ) : null}

        {parsedImport && parsedImport.errors.length > 0 ? (
          <div className="text-sm text-error-700 dark:text-error-300 bg-error-50 dark:bg-error-950/30 rounded-lg px-3 py-2 space-y-1 max-h-40 overflow-y-auto">
            {parsedImport.errors.map((err, i) => (
              <p key={i}>{err}</p>
            ))}
          </div>
        ) : null}

        {parsedImport && parsedImport.rows.length > 0 && parsedImport.errors.length === 0 ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Preview ({parsedImport.rows.length} rows{parsedImport.rows.length > 50 ? ', showing first 50' : ''})
            </p>
            <div className="overflow-x-auto max-h-48 border border-gray-200 dark:border-gray-700 rounded-lg">
              <Table className="min-w-[520px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>First name</TableHead>
                    <TableHead>Last name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Admission</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>DOB</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewRows.map((r: ParsedStudentCsvRow) => (
                    <TableRow key={r.sourceRowIndex}>
                      <TableCell>{r.sourceRowIndex}</TableCell>
                      <TableCell>{r.item.first_name}</TableCell>
                      <TableCell>{r.item.last_name ?? '—'}</TableCell>
                      <TableCell>{r.item.email ?? '—'}</TableCell>
                      <TableCell>{r.item.phone ?? '—'}</TableCell>
                      <TableCell>{r.item.admission_no ?? '—'}</TableCell>
                      <TableCell>{r.item.gender ?? '—'}</TableCell>
                      <TableCell>{r.item.dob ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}

        {importPreflight422 ? (
          <div className="text-sm text-error-700 dark:text-error-300 bg-error-50 dark:bg-error-950/30 rounded-lg px-3 py-2 space-y-1">
            <p className="font-medium">Import rejected (422)</p>
            {importPreflight422.map((e, i) => (
              <p key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</p>
            ))}
          </div>
        ) : null}

        {importApiResult ? (
          <div className="space-y-3 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Import results</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Requested: {importApiResult.summary.requested} · Created: {importApiResult.summary.created} · Skipped:{' '}
              {importApiResult.summary.skipped} · Failed: {importApiResult.summary.failed}
            </p>
            <div className="overflow-x-auto max-h-56">
              <Table className="min-w-[560px] text-sm">
                <TableHeader>
                  <TableRow>
                    <TableHead>Row</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Student ID</TableHead>
                    <TableHead>Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importApiResult.results.map((row, idx) => (
                    <TableRow
                      key={idx}
                      className={
                        row.status === 'created'
                          ? 'bg-green-50/80 dark:bg-green-950/20'
                          : row.status === 'failed'
                            ? 'bg-red-50/80 dark:bg-red-950/20'
                            : 'bg-amber-50/50 dark:bg-amber-950/10'
                      }
                    >
                      <TableCell>{row.index}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.name ?? '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{row.student_id ?? '—'}</TableCell>
                      <TableCell>{row.message ?? row.error_code ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" className="w-full sm:w-auto h-11" onClick={handleClose}>
            Close
          </Button>
          <Button type="submit" isLoading={isImporting} disabled={!importReady} className="w-full sm:w-auto h-11">
            Import
          </Button>
        </div>
      </form>
    </Modal>
  );
};
